import { useCallback, useMemo, useState } from 'react';
import { Typography } from '@mui/material';
import { useFeatureFlags } from 'hooks/useFeatureFlags';
import { useDispatch } from 'react-redux';
import { newToast } from 'store/slices/uiSlice';
import { ROUTES } from 'router';
import {
  useListPartnerProductsQuery,
  useParseCodesMutation,
  useImportCodesMutation,
  useListOrganisationCreationCodesQuery,
  useListLicenceCodesQuery,
  type ImportCodesResponse,
} from 'store/api/partnerProductCodes';

import {
  PartnerAdminDashboard as PartnerAdminDashboardPage,
  type PreviewRow,
} from '@karehero/llama/pwa/pages/PartnerAdminDashboard/PartnerAdminDashboard';

export const PartnerAdminDashboard = () => {
  const {
    flags: { isCaptureIFAEmail },
  } = useFeatureFlags();
  const dispatch = useDispatch();
  const [parseCodes, { isLoading: isParsing }] = useParseCodesMutation();
  const [importCodes, { isLoading: isUploading }] = useImportCodesMutation();

  // Fetch products for the authenticated RPA's partner
  const { data: products = [] } = useListPartnerProductsQuery();
  const productId = products[0]?.id ?? '';

  const { data: occCodes = [] } = useListOrganisationCreationCodesQuery(
    { productId },
    { skip: !productId },
  );

  const { data: lcCodes = [] } = useListLicenceCodesQuery(
    { productId },
    { skip: !productId },
  );

  // File preview state — owned by view so it survives page prop changes
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewRows, setPreviewRows] = useState<PreviewRow[]>([]);
  const [parseError, setParseError] = useState<string | null>(null);
  const [lastResult, setLastResult] = useState<ImportCodesResponse | null>(
    null,
  );
  const [uploadError, setUploadError] = useState<string | null>(null);

  const summary = useMemo(
    () => ({
      totalOrganisationCreationCodes: occCodes.length,
      redeemedOrganisationCreationCodes: occCodes.filter(
        (c) => c.status === 'redeemed',
      ).length,
      totalLicenceCodes: lcCodes.length,
      redeemedLicenceCodes: lcCodes.filter((c) => c.isRedeemed).length,
    }),
    [occCodes, lcCodes],
  );

  const handleFileSelected = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file || !productId) return;
      setUploadError(null);
      setLastResult(null);
      setParseError(null);

      try {
        const result = await parseCodes({ productId, file }).unwrap();
        setSelectedFile(file);
        setPreviewRows(result.rows);
      } catch (err) {
        const message =
          (err as { data?: { message?: string } })?.data?.message ||
          'Failed to parse CSV. Please try again.';
        setParseError(message);
        setSelectedFile(null);
        setPreviewRows([]);
      }
    },
    [productId, parseCodes],
  );

  const handleSubmit = useCallback(async () => {
    if (!selectedFile || !productId) return;
    setUploadError(null);
    try {
      const result = await importCodes({
        productId,
        file: selectedFile,
      }).unwrap();
      setLastResult(result);
      setSelectedFile(null);
      setPreviewRows([]);
    } catch {
      setUploadError('Upload failed. Please try again.');
    }
  }, [selectedFile, productId, importCodes]);

  const handleCancel = useCallback(() => {
    setSelectedFile(null);
    setPreviewRows([]);
    setParseError(null);
  }, []);

  const handleCopySignUpLink = useCallback(
    (code: string) => {
      const url = `${window.location.origin}${ROUTES.REGISTER}?partnerCode=${encodeURIComponent(code)}`;
      navigator.clipboard.writeText(url);
      dispatch(
        newToast({
          description: 'Sign-up link copied to clipboard',
          variant: 'success',
        }),
      );
    },
    [dispatch],
  );

  if (!productId) {
    return (
      <Typography
        sx={{
          color: 'text.secondary',
          p: 3,
        }}
      >
        Loading products…
      </Typography>
    );
  }

  return (
    <PartnerAdminDashboardPage
      summary={summary}
      organisationCreationCodes={occCodes}
      licenceCodes={lcCodes}
      onFileSelected={handleFileSelected}
      previewRows={previewRows}
      previewFileName={selectedFile?.name}
      parseError={parseError}
      onSubmit={handleSubmit}
      onCancel={handleCancel}
      isParsing={isParsing}
      isUploading={isUploading}
      lastResult={lastResult}
      uploadError={uploadError}
      isShowOrgOwnerEmail={!!isCaptureIFAEmail}
      onCopySignUpLink={handleCopySignUpLink}
    />
  );
};

PartnerAdminDashboard.displayName = 'PartnerAdminDashboard';
export default PartnerAdminDashboard;
